import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Header from "@/components/Header";
import AdminTabsNav from "@/components/AdminTabsNav";
import BlocklistManager from "@/components/BlocklistManager";
import AdminAccessManager from "@/components/AdminAccessManager";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Users, Loader2, Copy, Pause, Play, Ban, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtMicro(v) {
  return ((Number(v) || 0) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function trunc(a, n = 6) { return a ? `${a.slice(0, n)}…${a.slice(-4)}` : "—"; }
async function copyText(t) { try { await navigator.clipboard.writeText(t); toast.success("Copied"); } catch { /* noop */ } }

const AREA_COLORS = {
  perps: "text-orange-300 border-orange-500/30",
  bots: "text-purple-300 border-purple-500/30",
  agents: "text-cyan-300 border-cyan-500/30",
  flips: "text-yellow-300 border-yellow-500/30",
  vault: "text-green-300 border-green-500/30",
};

export default function AdminWallets() {
  const { getAuthHeaders, isConnected } = useWallet();
  const { showUsd, toggleUsd, stxoshiUsd } = useCurrency();
  const fmtBal = (micro) => {
    const whole = (Number(micro) || 0) / 1_000_000;
    if (showUsd) {
      const usd = whole * (stxoshiUsd || 0.0000037);
      return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: usd < 0.01 ? 8 : 2 })}`;
    }
    return whole.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };
  const unitLabel = showUsd ? "USD" : "STXOSHI";
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [deduping, setDeduping] = useState(false);

  const dedupeUsers = async () => {
    setDeduping(true);
    try {
      const preview = await (await fetch(`${API}/api/admin/users/dedupe?dry_run=true`, { method: "POST", headers: getAuthHeaders() })).json();
      if (!preview.duplicate_addresses) { toast.success("No duplicate user records found"); return; }
      if (!window.confirm(`Found ${preview.duplicate_addresses} wallet(s) with duplicate user records (${preview.users_removable} extra doc(s)). Merge them now? This reassigns their data to the primary record and removes the duplicates.`)) return;
      const res = await fetch(`${API}/api/admin/users/dedupe?dry_run=false`, { method: "POST", headers: getAuthHeaders() });
      const j = await res.json();
      if (res.ok) toast.success(`Merged ${j.users_removable} duplicate record(s) across ${j.duplicate_addresses} wallet(s)`);
      else toast.error(j.detail || "Dedupe failed");
    } catch { toast.error("Dedupe error"); }
    finally { setDeduping(false); load(); }
  };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/wallets?limit=1000`, { headers: getAuthHeaders() });
      if (res.ok) setData(await res.json());
      const ar = await fetch(`${API}/api/admin/audit-log?limit=200`, { headers: getAuthHeaders() });
      if (ar.ok) setAudit((await ar.json()).entries || []);
    } catch (e) { console.error("admin wallets load", e); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!isConnected) { setLoading(false); return; }
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isConnected, load]);

  const walletAction = async (w, action) => {
    const labels = {
      hold: `HOLD wallet ${trunc(w.address)}? Freezes ALL deposits and withdrawals until released.`,
      unhold: `Release hold on ${trunc(w.address)}?`,
      block: `BLOCK wallet ${trunc(w.address)} from deposits and withdrawals?`,
      unblock: `Unblock ${trunc(w.address)}?`,
    };
    if (!window.confirm(labels[action])) return;
    setActing(w.address + action);
    try {
      const res = await fetch(`${API}/api/admin/wallets/${w.address}/action`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const j = await res.json();
      if (res.ok && j.success) toast.success(`Wallet ${action} done`);
      else toast.error(j.detail || "Action failed");
    } catch { toast.error("Action error"); }
    finally { setActing(null); load(); }
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h1 className="text-2xl font-bold mb-2 flex items-center gap-2"><Users className="w-6 h-6 text-orange-500" /> User Wallets</h1>
          <AdminTabsNav active="wallets" />
          <Card className="bg-zinc-900 border-zinc-800"><CardContent className="py-12 text-center text-zinc-400" data-testid="admin-wallets-connect">
            Connect your admin wallet to view user wallets.
          </CardContent></Card>
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-wallets-page">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-orange-500" /> User Wallets</h1>
          <Button variant="outline" size="sm" className="border-amber-500/40 text-amber-300"
            onClick={dedupeUsers} disabled={deduping} data-testid="dedupe-users-btn">
            {deduping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Users className="w-4 h-4 mr-1" />}
            Dedupe Duplicate Users
          </Button>
        </div>
        <AdminTabsNav active="wallets" />

        {loading && !data ? (
          <div className="flex items-center gap-2 text-zinc-400 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Wallets</p>
                <p className="text-xl font-bold" data-testid="wallets-total">{totals.total_wallets || 0}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Total Balance</p>
                <p className="text-lg font-bold text-orange-400" data-testid="wallets-total-balance">{fmtBal(totals.total_balance_stxoshi)}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Blocked</p>
                <p className="text-xl font-bold text-red-400" data-testid="wallets-blocked-count">{totals.blocked_count || 0}</p>
              </CardContent></Card>
            </div>

            <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="admin-wallets-list">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-lg">All User Wallets</CardTitle>
                <button
                  onClick={toggleUsd}
                  className="text-xs px-2.5 py-1 rounded-full border border-zinc-600 text-zinc-300 hover:bg-zinc-700/60 transition-colors font-mono"
                  data-testid="admin-wallets-usd-toggle"
                  title="Toggle STXOSHI / USD"
                >
                  {showUsd ? "USD" : "STXOSHI"}
                </button>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[65vh] overflow-y-auto" data-testid="admin-wallets-scroll">
                {(data?.wallets || []).length === 0 && <p className="text-sm text-zinc-500 py-2">No wallets.</p>}
                {(data?.wallets || []).map((w) => {
                  const b = w.blocked;
                  const held = b && b.source === "wallet_hold";
                  return (
                    <div key={w.address} className="border border-zinc-800 rounded-lg p-3" data-testid={`wallet-row-${w.address}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <button className="font-mono text-sm text-zinc-200 inline-flex items-center gap-1 hover:text-white"
                            onClick={() => copyText(w.address)} title={w.address} data-testid={`wallet-addr-${w.address}`}>
                            {trunc(w.address, 8)} <Copy className="w-3 h-3" />
                          </button>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {b && (held
                              ? <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/40 text-[10px]">On Hold</Badge>
                              : <Badge className="bg-red-600/20 text-red-400 border-red-500/40 text-[10px]">Blocked</Badge>)}
                            <span className="text-[10px] text-zinc-500">{w.login_count} logins</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-orange-400">{fmtBal(w.balance_stxoshi)} <span className="text-[10px] font-normal">{unitLabel}</span></p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                        <div><span className="text-zinc-500">Deposits</span> {w.deposits_count} · <span className="text-green-400">{fmtBal(w.deposits_total_stxoshi)}</span></div>
                        <div><span className="text-zinc-500">Withdrawals</span> {w.withdrawals_count} · <span className="text-red-400">{fmtBal(w.withdrawals_total_stxoshi)}</span></div>
                      </div>

                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(w.interactions || {}).filter(([, n]) => n > 0).map(([area, n]) => (
                          <Badge key={area} variant="outline" className={`text-[10px] capitalize ${AREA_COLORS[area] || "text-zinc-300 border-zinc-600"}`}>
                            {area} {n}
                          </Badge>
                        ))}
                        {Object.values(w.interactions || {}).every((n) => n === 0) && (
                          <span className="text-[10px] text-zinc-600">no site activity</span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mt-3">
                        {held ? (
                          <Button size="sm" variant="outline" className="h-7 border-blue-500/40 text-blue-300 text-xs"
                            disabled={acting === w.address + "unhold"} onClick={() => walletAction(w, "unhold")}
                            data-testid={`wallet-unhold-${w.address}`}>
                            <Play className="w-3 h-3 mr-1" /> Unhold
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 border-blue-500/40 text-blue-300 text-xs"
                            disabled={acting === w.address + "hold"} onClick={() => walletAction(w, "hold")}
                            data-testid={`wallet-hold-${w.address}`}>
                            <Pause className="w-3 h-3 mr-1" /> Hold
                          </Button>
                        )}
                        {b && !held ? (
                          <Button size="sm" variant="outline" className="h-7 border-green-500/40 text-green-300 text-xs"
                            disabled={acting === w.address + "unblock"} onClick={() => walletAction(w, "unblock")}
                            data-testid={`wallet-unblock-${w.address}`}>
                            <ShieldCheck className="w-3 h-3 mr-1" /> Unblock
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 border-red-500/40 text-red-400 text-xs"
                            disabled={acting === w.address + "block"} onClick={() => walletAction(w, "block")}
                            data-testid={`wallet-block-${w.address}`}>
                            <Ban className="w-3 h-3 mr-1" /> Block
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Global manual block/unblock tool (same as bottom of Admin Flip) */}
            <BlocklistManager onChange={load} />

            {/* Admin access — grant/revoke admin privileges */}
            <AdminAccessManager />

            {/* Admin audit log */}
            <Card className="bg-zinc-900 border-zinc-800 mt-4" data-testid="admin-audit-log">
              <CardHeader className="pb-2"><CardTitle className="text-lg">Admin Audit Log</CardTitle></CardHeader>
              <CardContent>
                {audit.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-2">No admin actions recorded yet.</p>
                ) : (
                  <div className="space-y-1 max-h-[40vh] overflow-y-auto text-xs font-mono">
                    {audit.map((e) => (
                      <div key={e.id} className="flex flex-wrap justify-between gap-2 border-b border-zinc-800/50 py-1">
                        <span><span className="text-orange-400">{e.action}</span> {e.target ? `→ ${e.target.slice(0, 6)}…${e.target.slice(-4)}` : ""}</span>
                        <span className="text-zinc-500">{e.actor?.slice(-6)} · {e.timestamp ? new Date(e.timestamp).toLocaleString() : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
